/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { RefreshControl, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useTabWorkspaceLayout } from '../../shared/layout/useContentLayout';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { useBranchSettingsScreen } from './useBranchSettingsScreen';
import { DeliveryZonesSection } from './components/DeliveryZonesSection';
import { VUELTO_RIDERS_AVAILABLE } from '../domain/storeProfileTypes';
import type { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';

/** Hub: zonas y modos de delivery — layout Square. */
export function DeliverySettingsScreen() {
  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const vm = useBranchSettingsScreen();
  const deliveryGate = useCapabilityGate('delivery_fulfillment', {
    title: 'Delivery no disponible',
    subtitle: 'Este rubro no tiene entregas ni pedidos online.',
  });
  const { layoutWide } = useTabWorkspaceLayout();
  const navigation = useNavigation<any>();
  const { refreshing, onRefresh } = useScreenLoadShell('DeliverySettings', async (opts) => {
    await vm.load(opts);
  });

  const kpiItems = useMemo<TicketKpiItem[]>(() => {
    const ownOn = vm.storeProfileDraft.ownDeliveryEnabled;
    const ridersOn = vm.storeProfileDraft.vueltoRidersEnabled;
    return [
      {
        label: 'Envío local',
        value: ownOn ? 'Sí' : 'No',
        tone: ownOn ? 'ok' : 'default',
        testID: 'delivery.kpi.own',
      },
      // Riders oculto hasta que salga la app — si ya está ON se muestra para poder apagarlo.
      ...(VUELTO_RIDERS_AVAILABLE || ridersOn
        ? [
            {
              label: 'Repartidores',
              value: ridersOn ? 'Sí' : 'No',
              tone: ridersOn ? ('ok' as const) : ('default' as const),
              testID: 'delivery.kpi.riders',
            },
          ]
        : []),
      {
        label: 'Tienda online',
        value: vm.publicStoreFeaturesEnabled ? 'Sí' : 'No',
        tone: vm.publicStoreFeaturesEnabled ? 'ok' : 'warn',
        testID: 'delivery.kpi.store',
      },
    ];
  }, [vm.storeProfileDraft.ownDeliveryEnabled, vm.storeProfileDraft.vueltoRidersEnabled, vm.publicStoreFeaturesEnabled]);

  // ── Empty / permission states ──
  if (!deliveryGate.allowed) {
    return <ShellContentFrame style={ticketSurfaces.screen}>{deliveryGate.blocked}</ShellContentFrame>;
  }

  if (!vm.currentBranchId) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <ModuleEmptyState
          title="Seleccioná una sucursal"
          subtitle="Necesitás una sucursal activa para ver estos ajustes."
          primaryAction={{ label: 'Ir al selector', onPress: vm.handleOpenBranchSelector }}
        />
      </ShellContentFrame>
    );
  }

  if (vm.loading) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <View style={styles.loadingWrap}>
          <ModuleEmptyState title="Cargando…" subtitle="" />
        </View>
      </ShellContentFrame>
    );
  }

  if (!vm.canView) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <ModuleEmptyState
          title="Sin permiso"
          subtitle="Pedile al dueño que te habilite «Ajustes de sucursal» en permisos por empleado."
          primaryAction={{
            label: 'Volver',
            onPress: () => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Profile' as never)),
          }}
        />
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={ticketSurfaces.screen}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={ticketColors.gold}
            colors={[ticketColors.gold]}
          />
        }
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.stack} testID="delivery.screen">
          {/* Header Square */}
          <TicketModuleHeader
            kicker="Tienda online"
            title="Delivery y zonas"
            meta={
              VUELTO_RIDERS_AVAILABLE
                ? 'Activá el envío del local o repartidores independientes. Armá zonas con radio, tarifa y pedido mínimo.'
                : 'Activá el envío del local. Armá zonas con radio, tarifa y pedido mínimo.'
            }
            testID="delivery.header.sheet"
          />

          {/* KPIs */}
          <PaperSheet testID="delivery.kpi.sheet">
            <TicketKpiStrip
              items={kpiItems}
              layout={layoutWide ? 'desk' : 'mobile'}
              variant="soft"
            />
          </PaperSheet>

          {/* Solo lectura banner */}
          {!vm.canEditZones && !vm.canEditFees ? (
            <View style={styles.readOnlyBanner}>
              <View style={styles.readOnlyDot} />
              <Text style={styles.readOnlyText}>
                Solo lectura. Pedile permiso de edición al dueño.
              </Text>
            </View>
          ) : null}

          {/* Body: zonas o empty */}
          {vm.publicStoreFeaturesEnabled && vm.currentBranchId ? (
            <DeliveryZonesSection
              branchId={vm.currentBranchId}
              orgId={vm.orgId ?? ''}
              ownDeliveryEnabled={vm.storeProfileDraft.ownDeliveryEnabled}
              vueltoRidersEnabled={vm.storeProfileDraft.vueltoRidersEnabled}
              canEditZones={vm.canEditZones}
              canEditFees={vm.canEditFees}
              canToggle={vm.canToggleDelivery}
              onModesChange={(modes) =>
                vm.patchStoreProfileDraft({
                  ownDeliveryEnabled: modes.ownDeliveryEnabled,
                  vueltoRidersEnabled: modes.vueltoRidersEnabled,
                  deliveryEnabled: modes.ownDeliveryEnabled || modes.vueltoRidersEnabled,
                })
              }
            />
          ) : (
            <PaperSheet padded testID="delivery.empty.sheet">
              <ModuleEmptyState
                title="Tienda online desactivada"
                subtitle="Activá la tienda online para armar el envío y las zonas."
              />
            </PaperSheet>
          )}
        </View>
      </ScrollView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xxl,
  },
  stack: {
    gap: 10,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
  },
  readOnlyBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: ticketColors.paper,
    borderRadius: 10,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  readOnlyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: ticketColors.ink3,
    flexShrink: 0,
  },
  readOnlyText: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
  },
});
